import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './SkykitMonitor.css';
import Cookies from "universal-cookie";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const cookies = new Cookies();
const token = cookies.get("token");
const API_BASE_URL = process.env.REACT_APP_API || 'http://localhost:8000/jupiter';

const SkykitMonitor = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    tenantCode: 'all',
    success: 'all',
    tranId: '',
  });

  // Applied filters (separate state to track what's actually applied)
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: '',
    endDate: '',
    tenantCode: 'all',
    success: 'all',
    tranId: '',
  });

  const [showFilters, setShowFilters] = useState(false);

  // Build query string from APPLIED filters (not current filter state)
  const buildQueryString = useCallback((filtersToUse) => {
    const params = new URLSearchParams();
    if (filtersToUse.startDate) params.append('startDate', filtersToUse.startDate);
    if (filtersToUse.endDate) params.append('endDate', filtersToUse.endDate);
    if (filtersToUse.tenantCode && filtersToUse.tenantCode !== 'all') {
      params.append('tenantCode', filtersToUse.tenantCode);
    }
    if (filtersToUse.success && filtersToUse.success !== 'all') {
      params.append('success', filtersToUse.success);
    }
    if (filtersToUse.tranId) params.append('tranId', filtersToUse.tranId);

    const queryString = params.toString();
    console.log('🔍 Built query string:', queryString);
    console.log('🔍 Using filters:', filtersToUse);

    return queryString;
  }, []);

  // Fetch stats from API using applied filters
  const fetchStats = useCallback(async () => {
    try {
      console.log('📡 Fetching stats with applied filters:', appliedFilters);

      const queryString = buildQueryString(appliedFilters);
      const url = `${API_BASE_URL}/workOrders/skykit-logs/stats${queryString ? `?${queryString}` : ''}`;

      console.log('📡 Fetching URL:', url);

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      console.log('📥 Received stats:', data);

      if (data.status === 'success') {
        setStats(data.data);
        setError(null);
      } else {
        setError('Failed to fetch stats');
      }
    } catch (err) {
      console.error('❌ Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, buildQueryString]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    console.log(`🔄 Filter changed: ${key} = ${value}`);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters - this is the key function
  const handleApplyFilters = useCallback(() => {
    console.log('✅ Applying filters:', filters);
    setAppliedFilters({ ...filters }); // Copy filters to appliedFilters
    setLoading(true);
  }, [filters]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    console.log('🔄 Resetting filters');
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const resetFilters = {
      startDate: yesterday.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      tenantCode: 'all',
      success: 'all',
      tranId: '',
    };

    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setLoading(true);
  }, []);

  // Download logs with applied filters
  const handleDownload = useCallback(async () => {
    try {
      console.log('📥 Downloading logs with filters:', appliedFilters);

      const queryString = buildQueryString(appliedFilters);
      const url = `${API_BASE_URL}/workOrders/skykit-logs/download${queryString ? `?${queryString}` : ''}`;

      console.log('📥 Download URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;

      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = queryString ? '-filtered' : '';
      link.download = `skykit-logs${filterStr}-${dateStr}.jsonl`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      console.log('✅ Download complete');

    } catch (error) {
      console.error('❌ Download error:', error);
      alert('Failed to download logs. Please try again.');
    }
  }, [appliedFilters, buildQueryString]);

  // Clear logs
  const handleClearLogs = useCallback(async () => {
    if (!window.confirm('Are you sure you want to clear all logs?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/workOrders/skykit-logs`, {
        method: 'DELETE',
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.status === 'success') {
        alert('Logs cleared successfully');
        setLoading(true);
      }
    } catch (err) {
      alert('Failed to clear logs: ' + err.message);
    }
  }, []);

  // Set default date range on mount
  useEffect(() => {
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    const defaultFilters = {
      startDate: yesterday.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      tenantCode: 'all',
      success: 'all',
      tranId: '',
    };

    console.log('🎯 Setting default filters:', defaultFilters);
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  }, []);

  // Fetch when appliedFilters or loading state changes
  useEffect(() => {
    if (appliedFilters.startDate && appliedFilters.endDate && loading) {
      console.log('🔄 Fetching due to applied filters change or loading state');
      fetchStats();
    }
  }, [appliedFilters, loading, fetchStats]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (autoRefresh && !loading) {
      console.log('⏰ Setting up auto-refresh');
      intervalRef.current = setInterval(() => {
        console.log('⏰ Auto-refresh triggered');
        fetchStats();
      }, 30000);
    } else {
      if (intervalRef.current) {
        console.log('⏸️ Clearing auto-refresh');
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, loading, fetchStats]);

  if (loading) {
    return (
      <div className="skykit-monitor">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading Skykit stats...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="skykit-monitor">
        <div className="error-container">
          <div className="error-message">
            <strong>⚠️ Error:</strong> {error}
          </div>
          <button className="btn btn-primary" onClick={() => setLoading(true)}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="skykit-monitor">
        <div className="loading">No data available</div>
      </div>
    );
  }

  const successRate = stats.totalCalls > 0
    ? ((stats.successCalls / stats.totalCalls) * 100).toFixed(1)
    : 0;

  const peakMinute = stats.callsPerMinute && stats.callsPerMinute.length > 0
    ? Math.max(...stats.callsPerMinute.map(m => m.total))
    : 0;

  // Chart data
  const chartData = {
    labels: stats.callsPerMinute ? stats.callsPerMinute.map(item => {
      const timeStr = item.minute.includes('T')
        ? new Date(item.minute).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
        : item.minute.split(' ')[1]?.substring(0, 5) || item.minute;
      return timeStr;
    }) : [],
    datasets: [
      {
        label: 'Success',
        data: stats.callsPerMinute ? stats.callsPerMinute.map(item => item.success) : [],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgba(16, 185, 129, 1)',
        borderWidth: 1,
      },
      {
        label: 'Failure',
        data: stats.callsPerMinute ? stats.callsPerMinute.map(item => item.failure) : [],
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgba(239, 68, 68, 1)',
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    }
  };

  return (
    <div className="skykit-monitor">
      <div className="header-sky">
        <div className="header-content">
          <h1>🚀 Skykit API Call Monitor</h1>
          <p>Real-time tracking of Skykit API calls and success rates</p>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '🔽 Hide Filters' : '🔼 Show Filters'}
          </button>
          {stats.lastUpdated && (
            <div className="last-updated">
              <span className="pulse"></span>
              <span>Last: {stats.lastUpdated}</span>
            </div>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <h3>🔍 Filters</h3>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Tenant Code</label>
              <select
                value={filters.tenantCode}
                onChange={(e) => handleFilterChange('tenantCode', e.target.value)}
                className="filter-input"
              >
                <option value="all">All Tenants</option>
                {stats.tenantCodes && stats.tenantCodes.map(code => (
                  <option key={code || 'empty'} value={code}>{code || '(empty)'}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.success}
                onChange={(e) => handleFilterChange('success', e.target.value)}
                className="filter-input"
              >
                <option value="all">All</option>
                <option value="true">Success Only</option>
                <option value="false">Failures Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label>TranId (Search)</label>
              <input
                type="text"
                value={filters.tranId}
                onChange={(e) => handleFilterChange('tranId', e.target.value)}
                placeholder="SO12345"
                className="filter-input"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              ✓ Apply Filters
            </button>
            <button className="btn btn-secondary" onClick={handleResetFilters}>
              ✕ Reset
            </button>
          </div>

          {/* Show APPLIED filters, not current filter state */}
          {(appliedFilters.startDate || appliedFilters.endDate ||
            (appliedFilters.tenantCode && appliedFilters.tenantCode !== 'all') ||
            (appliedFilters.success && appliedFilters.success !== 'all') ||
            appliedFilters.tranId) && (
              <div className="active-filters">
                <strong>Active Filters:</strong>
                {appliedFilters.startDate && (
                  <span className="filter-tag">Start: {appliedFilters.startDate}</span>
                )}
                {appliedFilters.endDate && (
                  <span className="filter-tag">End: {appliedFilters.endDate}</span>
                )}
                {appliedFilters.tenantCode && appliedFilters.tenantCode !== 'all' && (
                  <span className="filter-tag">Tenant: {appliedFilters.tenantCode}</span>
                )}
                {appliedFilters.success && appliedFilters.success !== 'all' && (
                  <span className="filter-tag">
                    Status: {appliedFilters.success === 'true' ? 'Success' : 'Failure'}
                  </span>
                )}
                {appliedFilters.tranId && (
                  <span className="filter-tag">TranId: {appliedFilters.tranId}</span>
                )}
              </div>
            )}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon">📞</div>
          <div className="stat-content">
            <div className="stat-label">Total Calls</div>
            <div className="stat-value">{stats.totalCalls}</div>
            <div className="stat-subtitle">Filtered results</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-label">Success</div>
            <div className="stat-value">{stats.successCalls}</div>
            <div className="stat-subtitle">{successRate}% success rate</div>
          </div>
        </div>

        <div className="stat-card failure">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-label">Failures</div>
            <div className="stat-value">{stats.failureCalls}</div>
            <div className="stat-subtitle">{(100 - successRate).toFixed(1)}% failure rate</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📈</div>
          <div className="stat-content">
            <div className="stat-label">Peak Minute</div>
            <div className="stat-value">{peakMinute}</div>
            <div className="stat-subtitle">Highest calls/minute</div>
          </div>
        </div>
      </div>

      {stats.callsPerMinute && stats.callsPerMinute.length > 0 ? (
        <>
          <div className="chart-container">
            <h2>📊 Calls Per Minute</h2>
            <div className="chart-wrapper">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>

          <div className="table-container">
            <h2>📋 Detailed Breakdown</h2>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Total Calls</th>
                    <th>Success</th>
                    <th>Failures</th>
                    <th>Success Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.callsPerMinute.slice(-20).reverse().map((item, idx) => {
                    const rate = ((item.success / item.total) * 100).toFixed(0);
                    return (
                      <tr key={idx}>
                        <td>
                        {item.minute} EST
                        </td>
                        <td><strong>{item.total}</strong></td>
                        <td className="success-cell">{item.success}</td>
                        <td className="failure-cell">{item.failure}</td>
                        <td>
                          <span className="rate-badge">{rate}%</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>No Data for Selected Filters</h2>
          <p>Try adjusting your filter criteria or check if any API calls were made in this time period.</p>
        </div>
      )}

      <div className="actions-container">
        <h2>⚙️ Actions</h2>
        <div className="actions">
          <button
            className="btn btn-primary"
            onClick={() => setLoading(true)}
          >
            🔄 Refresh Now
          </button>

          <button
            className="btn btn-success"
            onClick={handleDownload}
            disabled={stats.totalCalls === 0}
          >
            📥 Download Logs
          </button>

          <button
            className={`btn ${autoRefresh ? 'btn-success' : 'btn-secondary'}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '⏸️ Stop Auto-Refresh' : '▶️ Start Auto-Refresh'}
          </button>

          <button
            className="btn btn-danger"
            onClick={handleClearLogs}
            disabled={stats.totalCalls === 0}
          >
            🗑️ Clear All Logs
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkykitMonitor;
